/**
 * Cross-component, in-memory store of person field edits keyed by person id.
 *
 * Used so that edits made anywhere — PeoplePage table cells, PersonDetailSheet,
 * Company/Deal drill-down sheets — are visible to every other consumer of
 * person data (e.g. MeetingBriefingSheet) within the same browser session.
 *
 * This is preview/demo-grade state. It is not persisted across reloads; the
 * production app uses Apollo cache + backend mutations for the same purpose.
 */

export interface PersonOverride {
  name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  jobTitle?: string;
  company?: string;
  email?: string;
  emails?: string[];
  phone?: string;
  phones?: string[];
  linkedin?: string;
  location?: string;
  avatar?: string;
}

const store = new Map<string, PersonOverride>();
const listeners = new Set<() => void>();

const notify = () => {
  for (const l of listeners) l();
};

export const personEditStore = {
  get(id: string | undefined | null): PersonOverride | undefined {
    if (!id) return undefined;
    return store.get(id);
  },
  set(id: string, override: PersonOverride): void {
    if (!id) return;
    store.set(id, override);
    notify();
  },
  /** Merge a partial override into any existing entry for this id. */
  merge(id: string, partial: PersonOverride): PersonOverride {
    if (!id) return partial;
    const next = { ...(store.get(id) || {}), ...partial };
    store.set(id, next);
    notify();
    return next;
  },
  clear(id: string): void {
    if (!id) return;
    store.delete(id);
    notify();
  },
  /** Return a person-shaped object with overrides merged on top. */
  apply<T extends { id?: string | null }>(person: T | null | undefined): T {
    if (!person || !person.id) return person as T;
    const override = store.get(person.id);
    if (!override) return person;
    return { ...person, ...override } as T;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
